import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '../axiosBaseQuery';

import { Country, CountrySelectOption, OrganisationCountryConfig, PreparationCentre } from './types';
import { CountryResponse, OrganisationCountryConfigResponse, PreparationCentreResponse } from './apiTypes';

export const countriesApi = createApi({
  reducerPath: 'countryApi',
  baseQuery: axiosBaseQuery({
    baseUrl: '/web-api/api',
  }),
  endpoints: (builder) => ({
    getCountries: builder.query<Country[], void>({
      query: () => ({ url: `/country`, method: 'get' }),
      transformResponse: (countries: CountryResponse[]) =>
        countries.map((country) => ({
          value: country.Id.toString(),
          name: country.Name,
          iso: country.IsoCode,
        })),
    }),
    getAllCountries: builder.query<CountrySelectOption[], void>({
      query: () => ({
        url: `/country/getCountry`,
        method: 'get',
      }),
      transformResponse: (countries: CountryResponse[]) =>
        countries.map((country) => ({
          id: country.Id.toString(),
          name: country.Name,
        })),
    }),
    getOrganisationCountryConfig: builder.query<OrganisationCountryConfig, number>({
      query: (countryId) => ({
        url: `/country/getOrganisationCountryConfig/${countryId}`,
        method: 'get',
      }),
      transformResponse: (config: OrganisationCountryConfigResponse) => ({
        allowAmendments: config.AllowAmendments,
        googleTagManagerCode: config.GoogleTagManagerCode,
        name: config.Name,
        paymentDeadlineDays: config.PaymentDeadlineDays,
        requiresExtraCandidateInfo: config.RequiresExtraCandidateInfo,
        showSpecialNeedsQuestion: config.ShowSpecialNeedsQuestion,
        urlBritishCouncilOffice: config.UrlBritishCouncilOffice,
        urlFindOutMoreAboutExamFees: config.UrlFindOutMoreAboutExamFees,
        urlHowDoIPay: config.UrlHowDoIPay,
        urlLearnAboutCancellationsAndRefunds: config.UrlLearnAboutCancellationsAndRefunds,
        urlNeedHelp: config.UrlNeedHelp,
        urlSpecialNeedsQuestionContactTheBc: config.UrlSpecialNeedsQuestionContactTheBc,
      }),
    }),
    getPreparationCentreInfo: builder.query<PreparationCentre, string>({
      query: (preparationCentreId) => ({
        url: `/country/getByPreparationCentre/${preparationCentreId}`,
        method: 'get',
      }),
      transformResponse: (data: PreparationCentreResponse) => ({
        awardingBodies: data.AwardingBodies,
        countryId: data.CountryId,
        disclaimer: data.Disclaimer,
        disclaimerEnabled: data.DisclaimerEnabled,
        districtId: data.DistrictId,
        isOnlinePayment: data.IsOnlinePayment,
        preparationCentreId: data.PreparationCentreId,
        preparationCentreName: data.PreparationCentreName,
      }),
    }),
  }),
});

export const {
  useGetCountriesQuery,
  useGetAllCountriesQuery,
  useLazyGetOrganisationCountryConfigQuery,
  useLazyGetPreparationCentreInfoQuery,
} = countriesApi;
